import { FC, useContext, useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import ServiceFilter from "./service-filters";
import PractitionerList from "lib/components/common/practitioner-list";
import FlexBox from "lib/components/common/flex-box";
import SimpleButton from "lib/components/common/simple-button";
import SimpleCard from "lib/components/common/simple-card";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import usePatientBookings from "lib/hooks/usePatientBookings";
import ErrorBox from "lib/components/common/error-box";
import { getItemFromArray } from "lib/utils/array-utils";
import {
  SessionBookingContext,
  useSessionBooking,
} from "lib/contexts/session-booking-context";
import { useNavigate } from "react-router-dom";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import useTherapies from "lib/hooks/useTherapies";
import usePackages from "lib/hooks/usePackages";
import useClinics from "lib/hooks/useClinics";
import { AppContext } from "lib/contexts/app-context";
import useApi from "lib/hooks/useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface PatientBookAppointmentProps {}

const PatientBookTherapySession: FC<PatientBookAppointmentProps> = ({}) => {
  const [sessionTherapies, setSessionTherapies] = useState<Therapy[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");
  const { error, loading } = usePatientBookings();
  const { getSessionEnabledTherapies } = useTherapies();
  const { getPackages, getPatientCredits } = usePackages();
  const { appState } = useContext(AppContext);
  const { postData } = useApi<CheckoutSessionResponse>();
  const { clinics, getClinics } = useClinics();
  const { updateSessionBookingState, sessionBookingState } = useSessionBooking();
  const navigateTo = useNavigate();

  useEffect(() => {
    getSessionEnabledTherapies((list) => setSessionTherapies(list));
    getPackages((list) => setPackages(list));
    getPatientCredits(appState.user_id, (res: any) => {
      setTotalCredits(Number(res.totalCreditsRemaining));
    });
    getClinics(true);
  }, []);

  const handleBuyPackage = (packageId: string) => {
    postData(
      API_ROUTES.PACKAGES.buy.replace(":packageId", packageId),
      {
        patientId: appState.user_id,
      },
      (response) => {
        console.log("package buy => response", response);
        window.location.href = response.url;
      }
    );
  };

  const handleBookSession = (therapyId: string) => {
    
    if (!selectedClinicId || selectedClinicId === "all") {
      alert("Please select a clinic before booking the session.");
      return;
    }

    updateSessionBookingState({
      ...sessionBookingState,
      clinicId: selectedClinicId,
      therapyId: therapyId,
      patientId: appState.user_id,
      patientName: appState.name,
    });
    navigateTo(PAGE_ROUTES.PATIENT.ConfirmTherapySession);
  };

  const handleClinicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClinicId(e.target.value);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading booking options...</p>
      </div>
    );
  }

  if (error) return <ErrorBox message={error} />;

  return (
    <Row>
      <Col xs={12} lg={4} className="mb-4 mb-lg-0">
        <div className="d-flex flex-column gap-3">
          <SimpleCard
            title="Select Clinic"
            className="shadow-sm"
            >
            {/** Filter only clinics where enableSessionBooking is TRUE */}
            {(() => {
              const sessionBookingClinics = clinics.filter(
                (c) => c.enableSessionBooking === true
              );

              return (
                <SimpleDropdown
                  showAllTitle="All Clinics"
                  options={sessionBookingClinics.map((c) => c.name)}
                  values={sessionBookingClinics.map((c) => c.id)}
                  selectedValue={selectedClinicId}
                  onChange={handleClinicChange}
                  label="Select Clinic"
                />
              );
            })()}
          </SimpleCard>
          {sessionTherapies.map((t) => (
            <SimpleCard
              key={t.id}
              className="therapy-item bg-light border-0 p-0 p-md-3 mb-2 text-start"
            >
              <FlexBox
                direction="row"
                alignItems="center"
                justifyContent="between"
                gap="2"
                className="flex-wrap"
              >
                <div className="col-12 col-md-6">
                  <span className="fw-bold text-primary">{t.name}</span>
                  <div className="small text-muted">1 session</div>
                  <div className="small text-muted">{t.duration} minutes</div>
                  <FlexBox alignItems="center" gap="2">
                    {t.price != null && <span className="me-2">£{t.price}</span>}
                    {t.credits != null && (
                      <span className="badge bg-primary-subtle text-primary">
                        {t.credits} credit{t.credits === 1 ? "" : "s"}
                      </span>
                    )}
                  </FlexBox>
                </div>
                <Button
                  className="w-md-auto mt-2 mt-md-0"
                  onClick={() => handleBookSession(t.id)}
                >
                  Book Session
                </Button>
              </FlexBox>
            </SimpleCard>
          ))}
        </div>
      </Col>

      <Col xs={12} lg={8}>
        <SimpleCard
          title={`Your credit balance: ${totalCredits}`}
          className="shadow-sm"
        >
          <FlexBox
            direction="row"
            gap="3"
            justifyContent="left"
            className="flex-wrap justify-content-center justify-content-lg-start"
          >
            {packages.map((p) => (
              <SimpleCard key={p.id} className="package-card mb-0 mb-md-3">
                <FlexBox
                  direction="column"
                  alignItems="center"
                  justifyContent="between"
                  className="h-100 p-0 p-md-4"
                >
                  <div className="w-100 text-center">
                    <h5 className="fw-bold text-primary">{p.credits} Credits</h5>
                    <h2 className="text-success fw-bold mb-2">£{p.price}</h2>
                    <p className="small text-muted mb-2">
                      {p.validity
                        ? `Valid until ${p.validity} months`
                        : "No expiry"}
                    </p>
                    <span className="badge bg-primary-subtle text-primary mb-3">
                      {p.name}
                    </span>
                  </div>
                  <Button className="" onClick={() => handleBuyPackage(p.id)}>
                    Buy Package
                  </Button>
                </FlexBox>
              </SimpleCard>
            ))}
          </FlexBox>
          <style>{`
            .package-card {
              min-width: 229px;
              max-width: 236px;
              flex: 1 1 260px;
              border-radius: 1.5rem;
            }
            .package-card:hover {
              box-shadow: 0 0 10px 0 #b6c6f5;
            }
            .btn-black-gradient {
              background: linear-gradient(90deg, #232526 0%, #414345 100%);
              color: #fff !important;
              border: none;
              font-weight: 600;
              letter-spacing: 0.5px;
              box-shadow: 0 2px 8px rgba(44,44,44,0.08);
              transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
            }
            .btn-black-gradient:hover {
              background: linear-gradient(90deg, #414345 0%, #232526 100%);
              color: #fff !important;
              box-shadow: 0 4px 16px rgba(44,44,44,0.12);
              transform: translateX(8px) scale(1.04);
            }
            .slide-btn {
              min-width: 140px;
              min-height: 48px;
              font-size: 1.1rem;
              border-radius: 12px;
              padding: 0.75rem 2rem;
              transition: transform 0.2s;
            }
          `}</style>
        </SimpleCard>
      </Col>
    </Row>
  );
};

export default PatientBookTherapySession;
